import { InsuranceQuoteContext } from "@/components/InsuranceQuoteLayout";
import Layout from "@/components/Layout";
import { AssurantQuoteV2 } from "@/types/Insurance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useLocation, useNavigate, useOutletContext } from "react-router";
import { useState } from "react";
import { Modal } from "../../components/Dialog";
import useLivlyUser from "@/context/UserProvider";
import {
  useDeleteInsuranceV2Quote,
  useUpdateInsuranceV2Quote
} from "./insurance-quote-v2";
import { Spinner } from "@/components/Spinner";

function getValue(data: any[], paramName: string): string | number {
  return data.find((p) => p.name === paramName)?.value;
}

const NotQualified = () => {
  const location = useLocation() as {
    state?: { denialNotice: string };
  };
  const navigate = useNavigate();
  const { data } = useOutletContext<InsuranceQuoteContext>();
  const { user } = useLivlyUser();
  const deleteAssuranceQuoteMutation = useDeleteInsuranceV2Quote(user);
  const updateAssurantQuoteMutation = useUpdateInsuranceV2Quote(user);

  const { parameters } = (data as AssurantQuoteV2).quoteResponse
    .coverageDetails;

  const denialNotice = location.state?.denialNotice || "";
  const liabilityAmount = getValue(parameters, "liabilityAmount");
  const is300K = liabilityAmount === "300000";
  const [showModal, setShowModal] = useState(false);

  const handleDeleteQuoteAndRedirect = async () => {
    const quoteRequest = (data as AssurantQuoteV2).quoteRequest;
    await deleteAssuranceQuoteMutation.mutateAsync();
    await updateAssurantQuoteMutation.mutateAsync({
      newRequest: {
        ...quoteRequest,
        transaction: {
          ...quoteRequest.transaction,
          referralId: Math.floor(
            1000000000 + Math.random() * 9000000000
          ).toString(), // 10-digit random
          workItemId: null
        }
      },
      SelectionMetaData: (data as AssurantQuoteV2).selectionMetaData
    });
    navigate("..");
  };

  return (
    <Layout
      title="Not Qualified"
      back={{
        label: "Liability Questions",
        to: -1
      }}
    >
      <div className="max-w-4xl mx-auto">
        <h2 className="font-medium text-2xl" data-test-id="summary">
          We’re sorry, but we’re unable to provide coverage for you at this time
        </h2>
        <div className="mt-4">
          <p>
            If you have been declined insurance coverage, it is because of the
            answer(s) you have provided to the question(s) on your application
            for insurance. We have based this decision solely on the information
            you have provided on your application.
          </p>
          {!is300K && (
            <p
              className="underline mt-2 cursor-pointer"
              onClick={() => setShowModal(true)}
            >
              Notice of Adverse Underwriting Decision
            </p>
          )}
          {showModal && (
            <Modal
              open={showModal}
              onClose={() => setShowModal(false)}
              title="Notice of Adverse Underwriting Decision"
            >
              <div className="mt-4">
                {denialNotice && (
                  <div
                    className="text-sm"
                    dangerouslySetInnerHTML={{ __html: denialNotice }}
                  />
                )}
              </div>
            </Modal>
          )}
        </div>
        <div className="mt-4">
          <p className="text-gray-600 mb-4">Here are your options:</p>

          {deleteAssuranceQuoteMutation.isLoading ||
          updateAssurantQuoteMutation.isLoading ? (
            <div className="flex items-center gap-2 mb-4">
              <Spinner color="blue" size="md" />{" "}
              <span className="text-gray-500">Redirecting, please wait...</span>
            </div>
          ) : (
            <div>
              {is300K && <UpdateLiability />}
              <UploadProof is300K={is300K} />
              <div className="border-t border-gray-300 ">
                <Link
                  to="../insurance-quote/liability-questions"
                  className="flex justify-between w-full items-start gap-4 md:p-4 md:rounded-lg md:hover:bg-gray-50"
                >
                  <p>Edit your answers</p>
                  <span className="text-gray-400">
                    <FontAwesomeIcon icon="chevron-right" />
                  </span>
                </Link>
              </div>
              <div className="border-t border-gray-300 ">
                <div
                  className="flex justify-between w-full items-start gap-4 md:p-4 md:rounded-lg md:hover:bg-gray-50"
                  onClick={handleDeleteQuoteAndRedirect}
                >
                  <p>Go back to Checklist & Retry</p>
                  <span className="text-gray-400">
                    <FontAwesomeIcon icon="chevron-right" />
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

function UpdateLiability() {
  return (
    <div className="mt-8 border-b border-gray-300 ">
      <Link
        to={`../insurance-quote/coverages`}
        state={{ label: "Insurance", returnTo: -1 }}
        className="flex items-start gap-4 md:p-4 md:rounded-lg md:hover:bg-gray-50"
      >
        <FontAwesomeIcon
          icon={["fas", "house-water"]}
          className="text-2xl text-[#F9877E]"
        />
        <div className="flex-1">
          <p className="font-medium">Update your liability</p>
          <p className="mt-1 text-sm">
            Edit your liability to see if you are qualified for $5,000 or
            $100,000.
          </p>
        </div>
        <FontAwesomeIcon icon="chevron-right" className="text-gray-400" />
      </Link>
    </div>
  );
}

function UploadProof({ is300K = false }: { is300K?: boolean }) {
  return (
    <div className={is300K ? "" : "mt-8 "}>
      <Link
        to={`../insurance/upload-proof`}
        state={{ label: "Insurance", returnTo: -1 }}
        className="flex items-start gap-4 md:p-4 md:rounded-lg md:hover:bg-gray-50"
      >
        {!is300K ? (
          <>
            <FontAwesomeIcon
              icon={["far", "arrow-up-to-line"]}
              className="text-2xl text-[#F9877E]"
            />
            <div className="flex-1">
              <p className="font-medium">Upload proof</p>
              <p className="mt-1 text-sm">
                Provide proof of insurance. Once uploaded, a property manager
                will review.
              </p>
            </div>
          </>
        ) : (
          <div className="flex-1">
            <p>Upload proof</p>
          </div>
        )}
        <FontAwesomeIcon icon="chevron-right" className="text-gray-400" />
      </Link>
    </div>
  );
}
export default NotQualified;
